import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionKey,
  type ReferralMemberIdentityDto,
  type ReferralPage,
  type ReferralRelationshipDto,
  type GuestAttributionDto,
  type ReferralRuleDto,
  type ReferralRewardPreviewDto,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';

const control = 'min-h-11 border border-base-300 rounded px-3 py-2 bg-base-100';
function MemberReferralsContent({
  memberId,
  memberNo,
  nickname,
}: {
  memberId: string;
  memberNo: string;
  nickname: string;
}) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const session = useAuthStore((s) =>
    JSON.stringify([s.user?.id, sessionVenueKey(s.accessToken), s.sessionRevision]),
  );
  const actions = useAuthStore((s) => s.allowedActions);
  const read = !!actions?.includes(ActionKey.MEMBER_REFERRAL_READ);
  const manage = !!actions?.includes(ActionKey.MEMBER_REFERRAL_MANAGE);
  const reconcileAllowed = !!actions?.includes(ActionKey.MEMBER_REFERRAL_RECONCILE);
  const [reconcileRequest, setReconcileRequest] = useState<{
    id: string;
    revision: number;
    reason: string;
    operationKey: string;
  } | null>(null);
  const rulesManage = !!actions?.includes(ActionKey.MEMBER_REFERRAL_RULES_MANAGE);
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<'guest' | 'referral'>('guest');
  const [number, setNumber] = useState('');
  const numberRef = useRef('');
  const [target, setTarget] = useState<ReferralMemberIdentityDto | null>(null);
  const [supersedesId, setSupersedesId] = useState('');
  const [reason, setReason] = useState('');
  const [operationKey, setOperationKey] = useState(() => crypto.randomUUID());
  const [tier, setTier] = useState('');
  const [base, setBase] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [ruleReason, setRuleReason] = useState('');
  const [ruleKey, setRuleKey] = useState(() => crypto.randomUUID());
  const [message, setMessage] = useState('');
  const prefix = ['member-referrals', session, memberId, page];
  const currentRelationship = useQuery({
    queryKey: ['member-referrals', session, memberId, 'current'],
    queryFn: () =>
      api.get<ReferralPage<ReferralRelationshipDto>>(`/members/${memberId}/referrals?page=1`),
    enabled: read,
  });
  const relationships = useQuery({
    queryKey: [...prefix, 'relationships'],
    queryFn: () =>
      api.get<ReferralPage<ReferralRelationshipDto>>(`/members/${memberId}/referrals?page=${page}`),
    enabled: read,
  });
  const guests = useQuery({
    queryKey: [...prefix, 'guests'],
    queryFn: () =>
      api.get<ReferralPage<GuestAttributionDto>>(
        `/members/${memberId}/guest-attributions?page=${page}`,
      ),
    enabled: read,
  });
  const rewards = useQuery({
    queryKey: [...prefix, 'rewards'],
    queryFn: () =>
      api.get<ReferralPage<ReferralRewardPreviewDto>>(
        `/members/${memberId}/referral-rewards?page=${page}`,
      ),
    enabled: read,
  });
  const rules = useQuery({
    queryKey: ['member-referrals', session, 'rules', page],
    queryFn: () => api.get<ReferralPage<ReferralRuleDto>>(`/members/referral-rules?page=${page}`),
    enabled: read,
  });
  const reconcile = useMutation({
    mutationFn: () =>
      api.post(`/members/referral-rewards/${reconcileRequest!.id}/reconcile`, {
        operationKey: reconcileRequest!.operationKey,
        reason: reconcileRequest!.reason.trim(),
        expectedRewardRevision: reconcileRequest!.revision,
      }),
    onSuccess: () => {
      setReconcileRequest(null);
      setMessage(t('referral.saved'));
      void qc.invalidateQueries({ queryKey: ['member-referrals'] });
    },
    onError: () => setMessage(t('referral.saveFailed')),
  });
  const change = () => {
    setOperationKey(crypto.randomUUID());
    setMessage('');
  };
  const lookup = useMutation({
    mutationFn: async () => {
      const found = await api.get<ReferralMemberIdentityDto>(
        `/members/referral-candidates?memberNo=${encodeURIComponent(number.trim())}`,
      );
      if (found.id === memberId || found.memberNo !== number.trim())
        throw new Error('REFERRAL_MEMBER_NOT_FOUND');
      return found;
    },
    onSuccess: (value) => {
      if (value.memberNo !== numberRef.current.trim()) return;
      setTarget(value);
      setMessage('');
    },
    onError: () => {
      setTarget(null);
      setMessage(t('referral.notFound'));
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/${kind === 'guest' ? 'guest-attributions' : 'referrals'}`, {
        operationKey,
        reason: reason.trim(),
        ...(kind === 'guest'
          ? { introducingMemberId: target!.id, ...(supersedesId ? { supersedesId } : {}) }
          : { uplineId: target!.id }),
      }),
    onSuccess: () => {
      setMessage(t('referral.saved'));
      setTarget(null);
      numberRef.current = '';
      setNumber('');
      setReason('');
      setSupersedesId('');
      setOperationKey(crypto.randomUUID());
      void qc.invalidateQueries({ queryKey: ['member-referrals'] });
    },
    onError: () => setMessage(t('referral.saveFailed')),
  });
  const [endRequest, setEndRequest] = useState<{
    id: string;
    revision: number;
    operationKey: string;
    reason: string;
  } | null>(null);
  const end = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/referrals/${endRequest!.id}/end`, {
        operationKey: endRequest!.operationKey,
        expectedRevision: endRequest!.revision,
        reason: endRequest!.reason,
      }),
    onSuccess: () => {
      setEndRequest(null);
      setMessage(t('referral.saved'));
      void qc.invalidateQueries({ queryKey: ['member-referrals'] });
    },
    onError: () => setMessage(t('referral.saveFailed')),
  });
  const saveRule = useMutation({
    mutationFn: () =>
      api.post('/members/referral-rules', {
        operationKey: ruleKey,
        reason: ruleReason.trim(),
        buyInTier: tier.trim(),
        baseOP: Number(base),
        effectiveAt: new Date(effectiveAt).toISOString(),
      }),
    onSuccess: () => {
      setMessage(t('referral.saved'));
      setBase('');
      setRuleReason('');
      setRuleKey(crypto.randomUUID());
      void qc.invalidateQueries({ queryKey: ['member-referrals'] });
    },
    onError: () => setMessage(t('referral.saveFailed')),
  });
  if (!read) return null;
  const busy = save.isPending || end.isPending || saveRule.isPending || reconcile.isPending;
  const ruleChanged = () => {
    setRuleKey(crypto.randomUUID());
    setMessage('');
  };
  return (
    <section
      className="border border-base-300 rounded-md p-3 space-y-2"
      aria-labelledby="member-referral-heading"
    >
      <h2 id="member-referral-heading" className="font-semibold">
        {t('referral.title')}
      </h2>
      <p className="font-medium">
        {t('referral.currentSummary')}:{' '}
        {currentRelationship.isError
          ? t('referral.loadFailed')
          : currentRelationship.isLoading
            ? t('referral.loading')
            : currentRelationship.data?.data
                .filter((row) => !row.endedAt)
                .map((row) => `${row.uplineMemberNo} · ${row.uplineNickname}`)
                .join(', ') || t('referral.noCurrent')}
      </p>
      <details className="space-y-3">
        <summary className="min-h-11 flex items-center cursor-pointer underline">
          {t('referral.details')}
        </summary>
        <p>{t('referral.noBenefit')}</p>
        <p className="text-warning">{t('referral.nonposting')}</p>
        {manage && (
          <fieldset disabled={busy} className="space-y-3">
            <legend>{t('referral.record')}</legend>
            <label className="block">
              {t('referral.kind')}{' '}
              <select
                className={control}
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as typeof kind);
                  change();
                }}
              >
                <option value="guest">{t('referral.guest')}</option>
                <option value="referral">{t('referral.enrollment')}</option>
              </select>
            </label>
            <p>{t('referral.associationAuthority')}</p>
            {kind === 'guest' && (
              <label className="block">
                {t('referral.corrects')}{' '}
                <select
                  className={control}
                  value={supersedesId}
                  onChange={(e) => {
                    setSupersedesId(e.target.value);
                    change();
                  }}
                >
                  <option value="">{t('referral.newAttribution')}</option>
                  {guests.data?.data.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.introducingMemberNo} · {row.createdAt}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block">
              {t('referral.memberNumber')}{' '}
              <input
                className={control}
                value={number}
                maxLength={32}
                onChange={(e) => {
                  numberRef.current = e.target.value;
                  setNumber(e.target.value);
                  setTarget(null);
                  change();
                }}
              />
            </label>
            <button
              type="button"
              className={control}
              disabled={!number.trim() || lookup.isPending}
              onClick={() => lookup.mutate()}
            >
              {t('referral.find')}
            </button>
            {target && (
              <p role="status">
                {t('referral.confirmIdentities', {
                  member: `${memberNo} · ${nickname}`,
                  introducer: `${target.memberNo} · ${target.nickname}`,
                })}
              </p>
            )}
            <label className="block">
              {t('referral.reason')}{' '}
              <input
                className={`${control} w-full`}
                value={reason}
                maxLength={500}
                onChange={(e) => {
                  setReason(e.target.value);
                  change();
                }}
              />
            </label>
            <button
              type="button"
              className={control}
              disabled={!target || !reason.trim() || busy || target.status !== 'ACTIVE'}
              onClick={() => save.mutate()}
            >
              {t('referral.confirm')}
            </button>
          </fieldset>
        )}
        {(relationships.isLoading || guests.isLoading || rewards.isLoading || rules.isLoading) && (
          <p role="status">{t('referral.loading')}</p>
        )}
        {(relationships.isError || guests.isError || rewards.isError || rules.isError) && (
          <p role="alert">
            {t('referral.loadFailed')}{' '}
            <button
              className={control}
              onClick={() => {
                void relationships.refetch();
                void guests.refetch();
                void rewards.refetch();
                void rules.refetch();
              }}
            >
              {t('referral.retry')}
            </button>
          </p>
        )}
        <h3 className="font-medium">{t('referral.relationships')}</h3>
        <ul>
          {relationships.data?.data.map((row) => (
            <li key={row.id} className="py-2 break-words">
              {row.uplineMemberNo} · {row.uplineNickname} · {row.effectiveAt} ·{' '}
              {row.endedAt ?? t('referral.current')} · {row.reason}{' '}
              {manage && !row.endedAt && (
                <button
                  className={control}
                  disabled={busy}
                  onClick={() =>
                    setEndRequest({
                      id: row.id,
                      revision: row.revision,
                      operationKey: crypto.randomUUID(),
                      reason: '',
                    })
                  }
                >
                  {t('referral.end')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {endRequest && (
          <fieldset disabled={busy} className="space-y-2">
            <legend>{t('referral.end')}</legend>
            <label>
              {t('referral.reason')}{' '}
              <input
                className={control}
                maxLength={500}
                value={endRequest.reason}
                onChange={(e) =>
                  setEndRequest({
                    ...endRequest,
                    reason: e.target.value,
                    operationKey: crypto.randomUUID(),
                  })
                }
              />
            </label>
            <button
              className={control}
              disabled={!endRequest.reason.trim()}
              onClick={() => end.mutate()}
            >
              {t('referral.confirm')}
            </button>
            <button className={control} onClick={() => setEndRequest(null)}>
              {t('referral.cancel')}
            </button>
          </fieldset>
        )}
        <h3 className="font-medium">{t('referral.guestHistory')}</h3>
        <ul>
          {guests.data?.data.map((row) => (
            <li className="py-2 break-words" key={row.id}>
              {row.introducingMemberNo} · {row.introducingNickname} · {row.createdAt} · {row.reason}
            </li>
          ))}
        </ul>
        <h3 className="font-medium">{t('referral.rewardPreview')}</h3>
        {!rewards.data?.data.length && !rewards.isLoading && <p>{t('referral.noRewards')}</p>}
        {rewards.data?.data.map((row) => (
          <article key={row.id} className="border border-base-300 p-3 space-y-2 break-words">
            <p>
              {t('referral.source')}: {row.sourceEventId} · {row.entryId}
            </p>
            <p>
              {t('referral.firstEntry')}:{' '}
              {row.firstEntryEvidence
                ? `${row.firstEntryEvidence.receiptId} · ${row.firstEntryEvidence.paymentId}`
                : t('referral.unavailable')}
            </p>
            <p>
              {t('referral.calculation')}:{' '}
              {row.calculation.base === null
                ? t('referral.unavailable')
                : `${row.calculation.base} · ${row.calculation.percentage ?? '?'}% · ${row.calculation.amount ?? '?'} OP`}
            </p>
            <p>
              {t('referral.evidence')}: {row.qualificationInstant ?? t('referral.unavailable')} ·{' '}
              {t('referral.relationshipVersion')} {row.relationshipRevision ?? '?'} ·{' '}
              {t('referral.ruleVersion')} {row.ruleRevision ?? '?'} ·{' '}
              {t('referral.participationVersion')} {row.participationRevision ?? '?'}
            </p>
            {row.firstEntryEvidence && (
              <details>
                <summary>{t('referral.qualificationEvidence')}</summary>
                <pre className="whitespace-pre-wrap break-all text-xs">
                  {JSON.stringify(row.firstEntryEvidence.facts, null, 2)}
                </pre>
              </details>
            )}
            <p>
              {t('referral.targetPosted')}: {row.targetTotal ?? 0} / {row.postedTotal ?? 0} OP ·{' '}
              {t('referral.proposedDelta')}: {row.proposedDelta ?? 0} OP · {row.state}
            </p>
            <p>
              {t('referral.hold')}:{' '}
              {row.holdReasons
                .map((code) => t(`referral.holds.${code}`, { defaultValue: code }))
                .join(' · ')}
            </p>
            {reconcileAllowed && row.state === 'HOLD' && (
              <button
                className={control}
                onClick={() =>
                  setReconcileRequest({
                    id: row.id,
                    revision: row.revision,
                    reason: '',
                    operationKey: crypto.randomUUID(),
                  })
                }
              >
                {t('referral.reconcile')}
              </button>
            )}
          </article>
        ))}
        {reconcileRequest && (
          <fieldset disabled={reconcile.isPending} className="space-y-2 border p-3">
            <legend>{t('referral.reconcile')}</legend>
            <p>{t('referral.reconcileNotice')}</p>
            <label>
              {t('referral.reason')}{' '}
              <input
                className={control}
                maxLength={500}
                value={reconcileRequest.reason}
                onChange={(e) =>
                  setReconcileRequest({
                    ...reconcileRequest,
                    reason: e.target.value,
                    operationKey: crypto.randomUUID(),
                  })
                }
              />
            </label>
            <button
              className={control}
              disabled={!reconcileRequest.reason.trim()}
              onClick={() => reconcile.mutate()}
            >
              {t('referral.confirm')}
            </button>
            <button className={control} onClick={() => setReconcileRequest(null)}>
              {t('referral.cancel')}
            </button>
          </fieldset>
        )}
        <h3 className="font-medium">{t('referral.rules')}</h3>
        <ul>
          {rules.data?.data.map((row) => (
            <li key={row.id}>
              {row.buyInTier} · {row.baseOP} OP · {row.effectiveAt} · v{row.version}
            </li>
          ))}
        </ul>
        {rulesManage && (
          <fieldset disabled={busy} className="space-y-2">
            <legend>{t('referral.newRule')}</legend>
            <label className="block">
              {t('referral.tier')}{' '}
              <input
                className={control}
                maxLength={64}
                value={tier}
                onChange={(e) => {
                  setTier(e.target.value);
                  ruleChanged();
                }}
              />
            </label>
            <label className="block">
              {t('referral.base')}{' '}
              <input
                className={control}
                type="number"
                min="0"
                max="2147483647"
                step="1"
                value={base}
                onChange={(e) => {
                  setBase(e.target.value);
                  ruleChanged();
                }}
              />
            </label>
            <label className="block">
              {t('referral.effectiveAt')}{' '}
              <input
                className={control}
                type="datetime-local"
                value={effectiveAt}
                onChange={(e) => {
                  setEffectiveAt(e.target.value);
                  ruleChanged();
                }}
              />
            </label>
            <label className="block">
              {t('referral.reason')}{' '}
              <input
                className={control}
                maxLength={500}
                value={ruleReason}
                onChange={(e) => {
                  setRuleReason(e.target.value);
                  ruleChanged();
                }}
              />
            </label>
            <button
              className={control}
              disabled={
                !tier.trim() ||
                base === '' ||
                !Number.isInteger(Number(base)) ||
                Number(base) < 0 ||
                Number(base) > 2147483647 ||
                !Number.isFinite(new Date(effectiveAt).getTime()) ||
                !ruleReason.trim()
              }
              onClick={() => saveRule.mutate()}
            >
              {t('referral.confirm')}
            </button>
          </fieldset>
        )}
        <div className="flex gap-2">
          <button className={control} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            {t('referral.previous')}
          </button>
          <span>{page}</span>
          <button
            className={control}
            disabled={
              !relationships.data?.hasMore &&
              !guests.data?.hasMore &&
              !rewards.data?.hasMore &&
              !rules.data?.hasMore
            }
            onClick={() => setPage((p) => p + 1)}
          >
            {t('referral.next')}
          </button>
        </div>
      </details>
      {message && <p role="status">{message}</p>}
    </section>
  );
}
export function MemberReferrals(props: Parameters<typeof MemberReferralsContent>[0]) {
  const identity = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
      props.memberId,
    ]),
  );
  return <MemberReferralsContent key={identity} {...props} />;
}
